import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '../_services/auth.service';
import { StorageService } from '../_services/storage.service';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';

const { expiryTokenTime } = environment;

@Injectable({
  providedIn: 'root'
})
export class InactivityService {

  private readonly INACTIVITY_LIMIT = expiryTokenTime; // e.g., 30 minutes
  private inactivityTimer: any = null;
  private countdownTimer: any = null;

  private isTracking = false;
  private routeSubscription: Subscription | null = null;

  private timeLeft = new BehaviorSubject<string>('30:00');
  timeLeft$ = this.timeLeft.asObservable();

  private userEvents = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
  private eventListenersAttached = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private storageService: StorageService
  ) {}

  /** ----------------------- START TRACKING ----------------------- */
  start(): void {
    if (this.isTracking) return;
    if (!this.storageService.isLoggedIn()) return; // ✅ Only for logged-in users

    this.isTracking = true;
    this.saveActivity();
    this.startCountdown();
    this.listenToUserEvents();
    this.listenToRouteChanges();

    console.log("Inactivity Tracking Started");
  }

  /** ----------------------- STOP TRACKING ----------------------- */
  stop(): void {
    this.isTracking = false;

    this.clearTimers();
    this.removeUserEvents();
    this.timeLeft.next('30:00');

    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
      this.routeSubscription = null;
    }

    this.removeActivity();
    console.debug("Inactivity Tracking Stopped");
  }

  /** ----------------------- USER EVENT LISTENERS ----------------------- */
  private listenToUserEvents(): void {
    if (this.eventListenersAttached) return;

    this.userEvents.forEach(evt =>
      window.addEventListener(evt, this.activityHandler)
    );

    this.eventListenersAttached = true;
  }

  private removeUserEvents(): void {
    if (!this.eventListenersAttached) return;

    this.userEvents.forEach(evt =>
      window.removeEventListener(evt, this.activityHandler)
    );

    this.eventListenersAttached = false;
  }

  private activityHandler = () => this.saveActivity();

  /** ----------------------- ROUTE CHANGES ----------------------- */
  private listenToRouteChanges(): void {
    this.routeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.saveActivity());
  }

  /** ----------------------- SAVE ACTIVITY ----------------------- */
  saveActivity(): void {
    if (!this.storageService.isLoggedIn()) return;

    localStorage.setItem('lastActivity', Date.now().toString());
    this.resetInactivityTimer();
  }

  private removeActivity(): void {
    localStorage.removeItem('lastActivity');
  }

  /** ----------------------- INACTIVITY TIMER ----------------------- */
  private resetInactivityTimer(): void {
    clearTimeout(this.inactivityTimer);

    this.inactivityTimer = setTimeout(() => {
      this.logoutUser('Logging out Due to Inactivity');
    }, this.INACTIVITY_LIMIT);
  }

  /** ----------------------- COUNTDOWN TIMER ----------------------- */
  private startCountdown(): void {
    clearInterval(this.countdownTimer);

    let remaining = this.INACTIVITY_LIMIT / 1000; // seconds

    this.countdownTimer = setInterval(() => {
      if (!this.isTracking || !this.storageService.isLoggedIn()) return;

      if (remaining <= 0) {
        clearInterval(this.countdownTimer);
        return;
      }

      remaining--;

      const min = Math.floor(remaining / 60).toString().padStart(2, '0');
      const sec = (remaining % 60).toString().padStart(2, '0');

      this.timeLeft.next(`${min}:${sec}`);

    }, 1000);
  }

  /** ----------------------- LOGOUT USER ----------------------- */
  private logoutUser(message: string): void {
    if (!this.storageService.isLoggedIn()) return;

    this.stop();

    localStorage.removeItem('accessToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('menuItemsFormatted');
    localStorage.removeItem('lastActivity');


    Swal.fire(message, '', 'error').then((result) => {
        if (result.isConfirmed) {
          this.authService.logout();
        }
        this.authService.logout();
      });

  }

  /** ----------------------- CLEAR TIMERS ----------------------- */
  private clearTimers(): void {
    clearTimeout(this.inactivityTimer);
    clearInterval(this.countdownTimer);

    this.inactivityTimer = null;
    this.countdownTimer = null;
  }
}
